import React, { Component } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import qs from 'qs';
import { map } from 'lodash/fp';
import { connect } from 'react-redux';
import { Link, withRouter } from 'react-router-dom';
import statePostalFips from 'config/statePostalFips';
import { actionUpdateFilter, actionSetUIFlag } from 'actions';
import { geographySelector } from 'selectors/indicator';
import {
  getHighlightForCategory,
  getLastSegment,
} from 'services/tableRowHelpers';
import styles from 'components/tableRow/tableRow.scss';
import getViewProperties from 'services/getViewProperties';

class TableRowChart extends Component {
  constructor() {
    super();

    this.maxSelections = 7;
    this.isChecked = this.isChecked.bind(this);
    this.onChange = this.onChange.bind(this);
    this.isDisabled = this.isDisabled.bind(this);
    this.renderCheckbox = this.renderCheckbox.bind(this);
  }

  onChange() {
    const {
      postal,
      basePostal,
      history,
      location: { pathname, search },
      tableFilter,
      updateFilter,
      setUIFlag,
    } = this.props;

    setUIFlag();

    const add = !this.isChecked();

    const newFilter = [...tableFilter];
    if (add) {
      newFilter.push(postal);
    } else {
      newFilter.splice(newFilter.indexOf(postal), 1);
    }

    if (0 === newFilter.length) {
      newFilter.push(basePostal);
    }

    updateFilter(newFilter, add ? postal : false);
    const query = qs.parse(search, { ignoreQueryPrefix: true });
    history.push({
      pathname,
      search: qs.stringify({
        ...query,
        geos: newFilter.join(','),
      }, {
        encoder: (str) => encodeURIComponent(str).replace(/%2C/g, ','),
      }),
    });
  }

  isDisabled() {
    const { tableFilter, match: { path } } = this.props;
    if ('chart' === getLastSegment(path)) {
      return (this.maxSelections <= tableFilter.length) && !this.isChecked();
    }
    return false;
  }

  isChecked() {
    const { basePostal, postal, tableFilter } = this.props;
    return (-1 < tableFilter.indexOf(postal)) ||
      (0 === tableFilter.length && basePostal === postal);
  }

  renderCheckbox({
    idx,
    fixed,
    formattedValue,
  }) {
    const {
      match: { params: { category, indicatorName } },
      location: { pathname },
      isTwoCol,
      renderType,
    } = this.props;

    const highlight = getHighlightForCategory(category);

    const { isMinimalView } = getViewProperties(renderType);

    return (
      <td
        key={idx}
        className={classNames(
          styles.cell,
          {
            [styles.fixed]: fixed && !isTwoCol,
            [styles.alignLeft]: isTwoCol,
            [styles.highlightedCell]:
              highlight.toLowerCase() === formattedValue.toLowerCase(),
          }
        )}
      >
        {!isMinimalView &&
          <input
            type="checkbox"
            checked={this.isChecked()}
            disabled={this.isDisabled()}
            onChange={this.onChange}
          />
        }
        {'table' === getLastSegment(pathname) &&
          'usdata' === category &&
          'United States' !== formattedValue &&
          'District of Columbia' !== formattedValue &&
          !isMinimalView ? (
            <Link
              className={styles.countyLink}
              to={`/${category}/indicator/${indicatorName}/table/${formattedValue}/counties`}
            >
              {formattedValue}
              <svg viewBox="0 0 24 24" tabIndex={-1}><path d="M8.59 16.58L13.17 12 8.59 7.41 10 6l6 6-6 6-1.41-1.42z" /></svg>
              <span className="screen-reader-text">View county level data</span>
            </Link>
          ) : (<span className={styles.innerCell}>{formattedValue}</span>)
        }
      </td>
    );
  }

  render() {
    const {
      rowId,
      data,
      postal,
      tableFilter,
      tableFilterToggle,
      pin,
      isTwoCol,
    } = this.props;

    if (tableFilterToggle && -1 === tableFilter.indexOf(postal)) {
      return false;
    }

    const first = data.slice(0, 1)[0];

    return (
      <tr
        key={rowId}
        className={classNames({
          [styles.tableRow]: true,
        })}
      >
        {this.renderCheckbox(first)}
        {data
          .slice(1)
          .sort(({ idx: a }, { idx: b }) => a - b)
          .map(({
            idx,
            fixed,
            formattedValue,
          }) => (
            <td
              key={idx}
              className={classNames(styles.cell, {
                [styles.fixed]: fixed && !isTwoCol,
                [styles.highlightedCell]: pin,
              })}
            >
              {formattedValue}
            </td>
          ))}
      </tr>
    );
  }
}

TableRowChart.defaultProps = {
  isTwoCol: false,
  renderType: '',
};

TableRowChart.propTypes = {
  postal: PropTypes.string.isRequired,
  basePostal: PropTypes.string.isRequired,
  tableFilter: PropTypes.arrayOf(PropTypes.string).isRequired,
  tableFilterToggle: PropTypes.bool.isRequired,
  history: PropTypes.any.isRequired,
  location: PropTypes.shape({
    search: PropTypes.string.isRequired,
    pathname: PropTypes.string.isRequired,
  }).isRequired,
  match: PropTypes.shape({
    params: PropTypes.shape({
      category: PropTypes.string.isRequired,
      indicatorName: PropTypes.string.isRequired,
    }).isRequired,
  }).isRequired,
  renderType: PropTypes.string,
  rowId: PropTypes.string.isRequired,
  pin: PropTypes.bool.isRequired,
  data: PropTypes.arrayOf(
    PropTypes.shape({
      idx: PropTypes.number.isRequired,
      fixed: PropTypes.oneOf(['left', 'right']),
      align: PropTypes.oneOf(['right', 'center', 'left']), // Currently not used
      formattedValue: PropTypes.string.isRequired,
    })
  ).isRequired,
  updateFilter: PropTypes.func.isRequired,
  setUIFlag: PropTypes.func.isRequired,
  isTwoCol: PropTypes.bool,
};

const mapStateToProps = (state) => ({
  tableFilter: state.data.tableFilter,
  tableFilterToggle: state.data.tableFilterToggle,
  basePostal: ('international' === state.data.indicator.dataView) ?
    'WORLD' :
    'US',
  geography: map(({ geography, code }) => ({
    geography,
    postal: ('international' === state.data.indicator.dataView) ?
      null :
      statePostalFips.find((datum) => datum[1] === code)[0],
  }))(geographySelector(state))
    .reduce((acc, { geography, postal }) => ({
      ...acc,
      [geography]: postal,
    }), {}),
});

const mapDispatchToProps = (dispatch) => ({
  updateFilter: (geos, add) => dispatch(actionUpdateFilter(geos, add)),
  setUIFlag: () => dispatch(actionSetUIFlag()),
});

const mergeProps = (
  {
    tableFilter,
    tableFilterToggle,
    basePostal,
    geography,
  },
  dispatch,
  ownProps
) => ({
  ...ownProps,
  ...dispatch,
  tableFilter,
  tableFilterToggle,
  basePostal,
  postal: geography[ownProps.data[0].formattedValue] ||
    ownProps.data[0].formattedValue,
});

export default withRouter(connect(
  mapStateToProps,
  mapDispatchToProps,
  mergeProps
)(TableRowChart));



// WEBPACK FOOTER //
// ./app/components/tableRowChart/index.js